// Translates Rebuy Dynamic Bundle widget (.rebuy-bundle-widget).
//
// Static text (supertitle, title, description, etc.) is handled
// server-side via Liquid in snippets/rebuy-template-bundle.liquid.
//
// Product titles are fetched from /products/{handle}.js which returns
// locale-appropriate data. Button text is translated via window.__rebuyI18n
// since buttonWidgetLabel() is controlled by Rebuy internally.

interface RebuyI18n {
	ctaLabel: string
	adding: string
	added: string
}

declare global {
	interface Window {
		__rebuyI18n?: RebuyI18n
	}
}

const BUNDLE_ANCHOR_ID = 'rebuy-bundle-builder'
const WIDGET_SELECTOR = '.rebuy-bundle-widget'
const BUNDLE_ANCHOR_LINK_SELECTOR = '[data-rebuy-bundle-anchor-link]'
const TITLE_CACHE = new Map<
	string,
	{ title: string; subtitle: string | null }
>()

let isTranslating = false
let debounceTimer: ReturnType<typeof setTimeout> | null = null

function splitTitle(fullTitle: string): {
	title: string
	subtitle: string | null
} {
	const idx = fullTitle.indexOf(' - ')
	if (idx === -1) return { title: fullTitle, subtitle: null }
	return {
		title: fullTitle.slice(0, idx),
		subtitle: fullTitle.slice(idx + 3),
	}
}

async function fetchTranslatedTitle(
	handle: string
): Promise<{ title: string; subtitle: string | null }> {
	const cached = TITLE_CACHE.get(handle)
	if (cached) return cached

	const res = await fetch(`/products/${handle}.js`)
	if (!res.ok) throw new Error(`Failed to fetch product ${handle}`)
	const product = await res.json()
	const result = splitTitle(product.title)
	TITLE_CACHE.set(handle, result)
	return result
}

async function translateProductTitleBlock(block: HTMLElement, handle: string) {
	try {
		const { title, subtitle } = await fetchTranslatedTitle(handle)

		const titleLink = block.querySelector('.rebuy-product-title-link')
		if (titleLink && titleLink.textContent?.trim() !== title.trim()) {
			titleLink.textContent = title
		}

		let subtitleEl = block.querySelector(
			'.rebuy-product-subtitle'
		) as HTMLElement | null
		if (subtitle) {
			if (!subtitleEl) {
				subtitleEl = document.createElement('span')
				subtitleEl.className = 'rebuy-product-subtitle'
				const titleEl = block.querySelector('h5.rebuy-product-title')
				if (titleEl) {
					titleEl.insertAdjacentElement('afterend', subtitleEl)
				}
			}
			if (subtitleEl.textContent?.trim() !== subtitle.trim()) {
				subtitleEl.textContent = subtitle
			}
		} else if (subtitleEl) {
			subtitleEl.remove()
		}
	} catch {
		// Keep Rebuy's original English title as fallback
	}
}

async function translateProductTitles(widget: Element) {
	const blocks = widget.querySelectorAll<HTMLElement>(
		'.rebuy-product-card[data-handle]'
	)
	const tasks: Promise<void>[] = []

	for (const block of blocks) {
		const handle = block.dataset.handle
		if (!handle) continue

		tasks.push(translateProductTitleBlock(block, handle))
	}

	await Promise.all(tasks)
}

function translateButtonText(widget: Element) {
	const i18n = window.__rebuyI18n
	if (!i18n) return

	const btn = widget.querySelector(
		'.rebuy-bundle__actions-buttons button.rebuy-button'
	)
	if (!btn) return

	const raw = btn.textContent?.trim() ?? ''
	if (!raw) return

	const text = raw.toLowerCase()
	const alreadyTranslated =
		raw === i18n.ctaLabel || raw === i18n.adding || raw === i18n.added

	if (alreadyTranslated) return

	let translated: string
	if (text.includes('adding')) {
		translated = i18n.adding
	} else if (text.includes('added')) {
		translated = i18n.added
	} else {
		translated = i18n.ctaLabel
	}

	btn.textContent = translated
}

async function translateWidget(widget: Element) {
	isTranslating = true
	try {
		translateButtonText(widget)
		await translateProductTitles(widget)
	} finally {
		isTranslating = false
	}
}

function scheduleTranslation() {
	if (debounceTimer) clearTimeout(debounceTimer)
	debounceTimer = setTimeout(() => {
		const widget = document.querySelector(WIDGET_SELECTOR)
		if (widget) translateWidget(widget)
	}, 100)
}

function scrollBundleBuilderIntoView(): boolean {
	const target = document.querySelector<HTMLElement>(`#${BUNDLE_ANCHOR_ID}`)
	if (!target) return false

	target.scrollIntoView({
		behavior: 'smooth',
		block: 'start',
	})
	return true
}

function scrollToBundleBuilder(event: MouseEvent) {
	event.preventDefault()
	if (scrollBundleBuilderIntoView()) return

	const observer = new MutationObserver(() => {
		if (scrollBundleBuilderIntoView()) observer.disconnect()
	})

	observer.observe(document.body, { childList: true, subtree: true })
	setTimeout(() => observer.disconnect(), 3000)
}

function initBundleAnchorLinks() {
	document.addEventListener('click', (event) => {
		if (!(event.target instanceof Element)) return
		if (!event.target.closest(BUNDLE_ANCHOR_LINK_SELECTOR)) return

		scrollToBundleBuilder(event)
	})
}

export function initRebuyBundleI18n() {
	initBundleAnchorLinks()

	const lang = document.documentElement.lang
	if (lang === 'en') return

	const observer = new MutationObserver(() => {
		if (isTranslating) return
		scheduleTranslation()
	})

	const existing = document.querySelector(WIDGET_SELECTOR)
	if (existing) {
		translateWidget(existing)
		observer.observe(existing, {
			childList: true,
			subtree: true,
			characterData: true,
		})
		return
	}

	const bodyObserver = new MutationObserver(() => {
		const widget = document.querySelector(WIDGET_SELECTOR)
		if (!widget) return

		bodyObserver.disconnect()
		translateWidget(widget)
		observer.observe(widget, {
			childList: true,
			subtree: true,
			characterData: true,
		})
	})

	bodyObserver.observe(document.body, { childList: true, subtree: true })
}
